import { occFetch, occUrl } from "~/app/api/occ/occFetch";
import type { MediaItem, MediaItemListResponseDTO } from "../types/mediaLibraryTypes";
import { mediaTransformer } from "./mediaTransformer";
import { SearchParamBuilder } from "~/utils/searchParamBuilder";

export type MediaAPIParams = {
  q?: string;
  page?: number;
  mediaTypes?: string[];
  productPortfolios?: string[];
  languages?: string[];
  segments?: string[];
  fetchFavourite?: boolean;
  showLatestVersion?: boolean;
  sortOrder?: "asc" | "desc";
  country?: string[];
};

export function generateDataFromUrl(): MediaAPIParams {
  const params = new URLSearchParams(window.location.search);

  return {
    q: params.get("q") ?? "",
    mediaTypes: params.get("mediaTypes")?.split(",") ?? [],
    productPortfolios: params.get("productPortfolios")?.split(",") ?? [],
    languages: params.get("languages")?.split(",") ?? [],
    country: params.get("country")?.split(",").filter(Boolean) ?? [],
    segments: params.get("segments")?.split(",") ?? [],
    fetchFavourite: params.get("fetchFavourite") === "true" ? true : false,
    sortOrder: params.get("asc") === "false" ? "desc" : "asc",
    showLatestVersion: params.get("showLatestVersion") !== "false",
  };
}

export function generateUrl(apiParams: MediaAPIParams, searchUrl?: string) {
  const url = new URL(window.location.href);
  const params = url.searchParams;

  const {
    q,
    mediaTypes,
    productPortfolios,
    languages,
    country,
    segments,
    fetchFavourite,
    showLatestVersion,
    sortOrder,
  } = apiParams;

  params.set("q", q ?? "");

  const setListParam = (key: string, list?: string[] | null, keepEmpty = false) => {
    if (list?.length) {
      params.set(key, list.join(","));
    } else {
      if (keepEmpty) {
        params.set(key, "");
      } else {
        params.delete(key);
      }
    }
  };
  setListParam("mediaTypes", mediaTypes);
  setListParam("productPortfolios", productPortfolios);
  setListParam("selectedLanguages", languages, true); // to handle user deselected the language from shop language preselection
  setListParam("segments", segments);

  if (fetchFavourite !== undefined) {
    params.set("fetchFavourite", String(fetchFavourite));
  }
  if (sortOrder !== undefined) {
    params.set("asc", String(sortOrder === "asc"));
  }

  if (searchUrl === "search-sds") {
    if (showLatestVersion !== undefined) {
      params.set("showLatestVersion", String(showLatestVersion));
    }
    if (country !== undefined) {
      params.set("country", String(country));
    }
  } else {
    params.delete("showLatestVersion");
    params.delete("country");
  }

  return url.toString();
}

export function getSearchEndpointParams(apiParams: MediaAPIParams, searchUrl: string): URLSearchParams {
  const {
    q,
    page = 0,
    mediaTypes,
    productPortfolios,
    languages,
    country,
    segments,
    fetchFavourite,
    showLatestVersion,
    sortOrder,
  } = apiParams;

  const builder = new SearchParamBuilder();

  builder.setIfDefined("q", q);
  builder.setIfDefined("page", page);

  builder.appendArrayParam("mediaTypes", mediaTypes);
  builder.appendArrayParam("productPortfolios", productPortfolios);
  builder.appendArrayParam("languages", languages);
  builder.appendArrayParam("segments", segments);

  builder.setIfDefined("fetchFavourite", fetchFavourite);

  if (searchUrl === "search-sds") {
    builder.setIfDefined("showLatestVersion", showLatestVersion);
    builder.appendArrayParam("country", country);
  }

  builder.setIfDefined("asc", sortOrder === "asc");

  return builder.build();
}

export async function fetchMediaAssets(params: MediaAPIParams, searchUrl: string): Promise<MediaItem[]> {
  window.history.replaceState({}, "", generateUrl(params, searchUrl));

  const searchParams = getSearchEndpointParams(params, searchUrl);
  const url = occUrl(`/media-library/${searchUrl}?${searchParams}`);

  const response = await occFetch(url);

  if (!response.ok) {
    throw new Error(`Error status: ${response.status}`);
  }

  const data = (await response.json()) as MediaItemListResponseDTO;
  return mediaTransformer(data.items);
}
